import { call, all, take, putResolve, put, takeEvery, select } from 'redux-saga/effects'

import {
  cancelTimerAction,
  startTimerAction,
  submitAlertAction,
  submitSMSAlert,
  submitTimeLineAction,
  updateLocationAction
} from '@redux/actions/safewalk'

import { channel } from 'redux-saga'

import * as localForage from 'localforage'

import dayjs from 'dayjs'

import {
  ATTEMPT_SUBMIT_ALERT_WITH_LOCATION,
  ATTEMPT_SUBMIT_ALERT,
  SAFEWALK_LOCATION_FETCH_SUCCESS,
  SAFEWALK_CONTACTS_SUBMIT_SUCCESS,
  SAFEWALK_SET_DATA,
  HIDE_ACTION_SHEET,
  LOCATION_FETCHING,
  SET_REDIRECT,
  UPDATE_LOADING
} from '@redux/actions/types'

const getSafeWalkData = state => state.safewalk
const getAuth = state => state.auth
const getLocation = state => state.location.currentLocation
const getIsAwaitingLocation = state => state.safewalk.isAwaitingLocation

const watchPositionChannel = channel()

/**
 * Builds and submits a SafeWalk alert with contacts, location, duration, and priority alert logic.
 */
function * attemptSubmitAlert() {
  const { accountConfig, userConfig } = yield select(getAuth)

  const safeWalkConfig = accountConfig?.safe_walk

  const safewalk = yield select(getSafeWalkData)

  const {
    alarmTriggeredManually,
    formDuration,
    isOffHours,
    isOnCampus,
    nextState
  } = safewalk

  const {
    alert_on_off_site_walk: alertOnOffSiteWalk,
    alert_on_start_walk: alertOnStartWalk,
    enable_safe_walk: enableSafeWalk,
    alert_on_weekend_walk: alertOnWeekendWalk
  } = safeWalkConfig

  if (!enableSafeWalk || !nextState) return

  const auth = yield select(getAuth)

  const coords = yield select(getLocation)

  const contacts = userConfig?.emergency_contacts || []

  const activeWalkId = userConfig?.safe_walk?.current_active_walk_id
  const timerId = userConfig?.safe_walk?.timer_id
  const messageId = userConfig?.safe_walk?.lucy_message_id

  const isWeekend = dayjs().day() === 6 || dayjs().day() === 0
  const shouldAlertOnWeekend = alertOnWeekendWalk && isWeekend

  const sendPriorityAlert = () => {
    if (shouldAlertOnWeekend && !isOffHours) return true

    if (alertOnOffSiteWalk) return true

    if (isOffHours) return false

    if (!isOnCampus) return false

    return true
  }

  let location = null

  if (nextState !== 'warning') {
    const { latitude: lat, longitude: lng } = coords
    location = { lat, lng }
  }

  const hours = dayjs.duration(formDuration, 'ms').hours()
  const minutes = dayjs.duration(formDuration, 'ms').minutes()

  const formatedDuration = {
    hrs: hours,
    mins: minutes
  }

  const data = {
    contacts: contacts.map((contact) => ({ number: contact.internationalNumber })),
    user_has_contacts: !!contacts.length,
    duration: formatedDuration,
    location,
    state: nextState,
    alarm_triggered_manually: alarmTriggeredManually,
    doc_id: activeWalkId,
    lucy_message_id: messageId,
    send_pa: [
      { state: 'walk-activated', send_pa: false },
      { state: 'alarm-active', send_pa: false }
    ]
  }

  const actions = []

  if (sendPriorityAlert() && nextState !== 'warning') {
    if (alertOnStartWalk) {
      if (nextState === 'walk-activated' || nextState === 'alarm-active') {
        data.send_pa[0].send_pa = true
        data.send_pa[1].send_pa = true
        actions.push(submitTimeLineAction({ auth, data }))
      } else if (activeWalkId) {
        actions.push(submitAlertAction({ auth, data }))
      }
    } else {
      data.send_pa[1].send_pa = true
      if (nextState !== 'walk-activated' && nextState !== 'walk-ended') {
        if (nextState === 'alarm-active' && !activeWalkId) {
          actions.push(submitTimeLineAction({ auth, data }))
        } else {
          actions.push(submitAlertAction({ auth, data }))
        }
      }
    }
  }

  actions.push(submitSMSAlert({ auth, data }))

  const queuedActions = actions.map((action) => putResolve(action))

  try {
    if (nextState === 'walk-activated') {
      yield putResolve(startTimerAction({ auth, data }))
    }

    if (nextState === 'alarm-cancelled' || nextState === 'walk-ended') {
      yield putResolve(cancelTimerAction({ auth, data, timerId }))
    }

    yield all(queuedActions)
  } catch (e) {
    console.log('error', e)
  } finally {
    yield put({
      type: UPDATE_LOADING,
      payload: {
        loadingAction: 'unset',
        meta: null
      }
    })
    if (nextState === 'walk-activated') {
      const pac = auth.hash.substring(0, 10)
      yield put({ type: SET_REDIRECT, payload: `/${pac}/safewalk` })
    }
  }
}

/**
 * Initiates a SafeWalk alert submission by storing walk data and fetching location first.
 * @param action
 */
function * attemptSubmitAlertWithLocation(action) {
  const { payload } = action
  const auth = yield select(getAuth)
  const isAwaitingLocation = yield select(getIsAwaitingLocation)

  if (isAwaitingLocation) return

  yield put({
    type: SAFEWALK_SET_DATA,
    payload
  })

  yield put({
    type: LOCATION_FETCHING,
    payload: ATTEMPT_SUBMIT_ALERT
  })

  if (payload.nextState === 'walk-activated') {
    const errorHandler = (err) => {
      if (err.code === 1) {
        console.log('Error: Access is denied!')
      } else if (err.code === 2) {
        console.log('Error: Position is unavailable!')
      }
    }
    
    const watchPositionId = yield call(() => navigator.geolocation.watchPosition(
      ({ coords }) => {
        const { latitude: lat, longitude: lng } = coords
        const location = { lat, lng }
        watchPositionChannel.put(updateLocationAction({ auth, data: { location } }))
      }, errorHandler, { timeout: 60000 }))
      
    yield localForage.setItem('watchPositionId', watchPositionId)
  }
}

/**
 * Hides the action sheet after emergency contacts are added successfully.
 */
function * onAddContactsSuccess() {
  yield put({ type: HIDE_ACTION_SHEET })
}

/**
 * Dispatches an API call to update the SafeWalk location after a successful location fetch.
 */
function * onUpdateLocationSuccess() {
  const auth = yield select(getAuth)
  const coords = yield select(getLocation)

  const { latitude: lat, longitude: lng } = coords

  const location = { lat, lng }

  yield put(updateLocationAction({ auth, data: { location } }))
}

/**
 * Root saga that watches for SafeWalk-related actions and position updates.
 */
function * watchSafeWalk() {
  yield takeEvery(ATTEMPT_SUBMIT_ALERT_WITH_LOCATION, attemptSubmitAlertWithLocation)
  yield takeEvery(SAFEWALK_CONTACTS_SUBMIT_SUCCESS, onAddContactsSuccess)
  yield takeEvery(SAFEWALK_LOCATION_FETCH_SUCCESS, onUpdateLocationSuccess)
  yield takeEvery(ATTEMPT_SUBMIT_ALERT, attemptSubmitAlert)
  while (true) {
    const action = yield take(watchPositionChannel)
    yield put(action)
  }
}

export default watchSafeWalk
